/**
 * Native WebAudio "chime" for the scanner — a short two-tone confirmation
 * that works in loud cafés where the visual flash gets missed.
 *
 * Design: warm sine-wave arpeggio (700Hz → 1050Hz), 120ms total, gentle 0.22
 * gain envelope. NOT abrasive. Deliberately shorter than a shop bell so it
 * blends into the ambient counter noise rather than announcing itself.
 *
 * The context is created lazily on first use and reused — creating a new
 * AudioContext per stamp would leak on slower Android devices.
 */
let _ctx = null;
let _unlocked = false;

function ctx() {
  if (_ctx) return _ctx;
  try {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch (_) {
    _ctx = null;
  }
  return _ctx;
}

/** Call synchronously inside a tap/click. iOS only lets audio start from a user gesture,
 *  so we resume the context + play one silent sample here; later async chimes then work. */
export function unlockAudio() {
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  if (_unlocked) return;
  try {
    const src = c.createBufferSource();
    src.buffer = c.createBuffer(1, 1, 22050);
    src.connect(c.destination);
    src.start(0);
    _unlocked = true;
  } catch (_) {}
}

function vibrate(pattern) {
  try { if (navigator.vibrate) navigator.vibrate(pattern); } catch (_) {}
}

function tone({ freq, start, duration, gain = 0.22 }) {
  const c = ctx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, c.currentTime + start);
  g.gain.setValueAtTime(0.0001, c.currentTime + start);
  g.gain.exponentialRampToValueAtTime(gain, c.currentTime + start + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + start + duration);
  osc.connect(g).connect(c.destination);
  osc.start(c.currentTime + start);
  osc.stop(c.currentTime + start + duration + 0.02);
}

export function playSuccessChime() {
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  tone({ freq: 720, start: 0.00, duration: 0.09 });
  tone({ freq: 1080, start: 0.08, duration: 0.12, gain: 0.24 });
}

export function playRedeemChime() {
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  // A more celebratory 3-note lift for reward-unlocked / redeem events.
  tone({ freq: 660, start: 0.00, duration: 0.10 });
  tone({ freq: 880, start: 0.08, duration: 0.10 });
  tone({ freq: 1320, start: 0.16, duration: 0.14, gain: 0.24 });
}

export function playErrorChime() {
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  // Deliberately soft — a muted 320Hz "nope" thud so staff doesn't feel
  // yelled at when they mis-scan. Loud enough to hear, quiet enough to not
  // embarrass the customer.
  tone({ freq: 320, start: 0.00, duration: 0.14, gain: 0.18 });
}

/** Chime + haptic in one call. Haptics fire on Android; iOS Safari has no web vibration API. */
export function cueSuccess() { vibrate([30, 40, 30]); playSuccessChime(); }
export function cueRedeem() { vibrate([30, 40, 30, 40, 60]); playRedeemChime(); }
export function cueError() { vibrate(80); playErrorChime(); }
export function cueDuplicate() { vibrate([20, 30, 20]); playErrorChime(); }
